import { useSearchParams } from 'react-router-dom';
import { ChatHistoryLeaderboard } from './components/ChatHistoryLeaderboard';
import { QuestionsStats } from './components/QuestionsStats';
import { SttSessionsLeaderboard } from './components/SttSessionsLeaderboard';
import { TriggerLogsLeaderboard } from './components/TriggerLogsLeaderboard';
import { UserGrowthAnalytics } from './components/UserGrowthAnalytics';
import { VoiceConversationsLeaderboard } from './components/VoiceConversationsLeaderboard';
import { VoiceUsageStats } from './components/VoiceUsageStats';

const TABS = [
    { id: 'trigger-logs', label: 'Trigger Logs Leaderboard', icon: '📊' },
    { id: 'stt-sessions', label: 'STT Sessions', icon: '🎙️' },
    { id: 'questions', label: 'Questions', icon: '📚' },
    { id: 'voice-conversations', label: 'Voice Conversations', icon: '🗣️' },
    { id: 'voice-usage', label: 'Voices', icon: '🔊' },
    { id: 'user-growth', label: 'User Growth', icon: '📈' },
    { id: 'chat-history', label: 'Chat History', icon: '💬' },
] as const;

type StatsTab = (typeof TABS)[number]['id'];

const DEFAULT_TAB: StatsTab = 'trigger-logs';

const isValidTab = (tab: string | null): tab is StatsTab =>
    TABS.some((t) => t.id === tab);

export const Stats = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const activeTab: StatsTab = isValidTab(tabParam) ? tabParam : DEFAULT_TAB;

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Stats</h1>
                <p className="text-gray-600">User statistics and leaderboards</p>
            </div>

            {/* Tab Navigation */}
            <div className="mb-6">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex flex-wrap gap-x-4 sm:gap-x-8">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setSearchParams({ tab: tab.id })}
                                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                    activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                <span className="mr-2">{tab.icon}</span>
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {activeTab === 'trigger-logs' && <TriggerLogsLeaderboard />}
            {activeTab === 'stt-sessions' && <SttSessionsLeaderboard />}
            {activeTab === 'questions' && <QuestionsStats />}
            {activeTab === 'voice-conversations' && <VoiceConversationsLeaderboard />}
            {activeTab === 'voice-usage' && <VoiceUsageStats />}
            {activeTab === 'user-growth' && <UserGrowthAnalytics />}
            {activeTab === 'chat-history' && <ChatHistoryLeaderboard />}
        </div>
    );
};
